import NoteDetailModal from "@/components/Modals/NoteDetailModal";
import { useEmployee } from "@/contexts/EmployeeProvider";
import {
    type NotificationHistory,
    useFetchNotificationHistory,
} from "@/queries/useFetchNotificationHistory";
import CalendarIcon from "@mui/icons-material/CalendarToday";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import InfoIcon from "@mui/icons-material/Info";
import BellIcon from "@mui/icons-material/Notifications";
import PersonIcon from "@mui/icons-material/Person";
import WarningIcon from "@mui/icons-material/Warning";
import {
    Alert,
    Box,
    Button,
    Card,
    CardContent,
    Chip,
    Grid2,
    Link,
    MenuItem,
    Stack,
    TextField,
    Typography,
} from "@mui/material";
import { download, generateCsv, mkConfig } from "export-to-csv";
import {
    type MRT_ColumnDef,
    MaterialReactTable,
    useMaterialReactTable,
} from "material-react-table";
import { useCallback, useMemo, useState } from "react";

const csvConfig = mkConfig({
    fieldSeparator: ",",
    decimalSeparator: ".",
    useKeysAsHeaders: true,
    filename: `notification-history-${new Date().toISOString().split("T")[0]}`,
});

interface SelectedNotification {
    noteId: string | null;
    caseNumber: string;
    clientName: string;
}

const NotificationHistoryPage = () => {
    const { hasAnyPermission } = useEmployee();
    const isAdmin = hasAnyPermission(["create:employee", "delete:employee", "edit:employee"]);

    const [searchQuery, setSearchQuery] = useState("");
    const [priorityFilter, setPriorityFilter] = useState("all");
    const [statusFilter, setStatusFilter] = useState("all");
    const [fromDate, setFromDate] = useState("");
    const [toDate, setToDate] = useState("");

    // Note Detail Modal state
    const [isNoteModalOpen, setIsNoteModalOpen] = useState(false);
    const [selectedNotification, setSelectedNotification] = useState<SelectedNotification | null>(
        null,
    );

    const handleCaseClick = (notification: NotificationHistory) => {
        if (notification.noteId) {
            setSelectedNotification({
                noteId: notification.noteId,
                caseNumber: notification.caseNumber,
                clientName: notification.clientName,
            });
            setIsNoteModalOpen(true);
        }
    };

    const handleCloseNoteModal = () => {
        setIsNoteModalOpen(false);
        setSelectedNotification(null);
    };

    const {
        data: notifications = [],
        isError,
        isRefetching,
        isLoading,
    } = useFetchNotificationHistory();

    // Filter notifications based on search and filters
    const filteredData = useMemo(() => {
        let filtered = [...notifications];

        // Search filter
        if (searchQuery) {
            const query = searchQuery.toLowerCase();
            filtered = filtered.filter(
                (n) =>
                    n.title.toLowerCase().includes(query) ||
                    n.caseNumber.toLowerCase().includes(query) ||
                    n.clientName.toLowerCase().includes(query) ||
                    n.sentBy.toLowerCase().includes(query) ||
                    n.recipients.some((r) => r.toLowerCase().includes(query)),
            );
        }

        // Priority filter
        if (priorityFilter !== "all") {
            filtered = filtered.filter((n) => n.priority === priorityFilter);
        }

        // Status filter
        if (statusFilter !== "all") {
            filtered = filtered.filter((n) => n.status === statusFilter);
        }

        // Date range filters
        if (fromDate) {
            const from = new Date(fromDate);
            filtered = filtered.filter((n) => new Date(n.sentAt) >= from);
        }

        if (toDate) {
            const to = new Date(toDate);
            to.setHours(23, 59, 59, 999); // End of day
            filtered = filtered.filter((n) => new Date(n.sentAt) <= to);
        }

        return filtered;
    }, [notifications, searchQuery, priorityFilter, statusFilter, fromDate, toDate]);

    // Calculate statistics
    const stats = useMemo(() => {
        const total = filteredData.length;
        const unread = filteredData.filter((n) => n.status === "unread").length;
        const high = filteredData.filter((n) => n.priority === "high").length;
        const medium = filteredData.filter((n) => n.priority === "medium").length;
        const low = filteredData.filter((n) => n.priority === "low").length;

        return { total, unread, high, medium, low };
    }, [filteredData]);

    // Format date/time for display
    const formatDateTime = useCallback((dateString: string) => {
        const date = new Date(dateString);
        return date.toLocaleString("en-US", {
            month: "2-digit",
            day: "2-digit",
            year: "numeric",
            hour: "2-digit",
            minute: "2-digit",
            hour12: true,
        });
    }, []);

    // Column definitions
    const columns = useMemo<MRT_ColumnDef<NotificationHistory>[]>(
        () => [
            {
                accessorKey: "priority",
                header: "Priority",
                size: 120,
                Cell: ({ row }) => {
                    const priority = row.original.priority;
                    const configs = {
                        high: {
                            color: "error" as const,
                            icon: <WarningIcon fontSize="small" />,
                            label: "HIGH",
                        },
                        medium: {
                            color: "warning" as const,
                            icon: <InfoIcon fontSize="small" />,
                            label: "MEDIUM",
                        },
                        low: {
                            color: "success" as const,
                            icon: <CheckCircleIcon fontSize="small" />,
                            label: "LOW",
                        },
                    };
                    const config = configs[priority];
                    return (
                        <Chip
                            icon={config.icon}
                            label={config.label}
                            color={config.color}
                            size="small"
                        />
                    );
                },
            },
            {
                accessorKey: "title",
                header: "Title",
                size: 250,
                Cell: ({ row }) => (
                    <Box display="flex" alignItems="center" gap={1}>
                        <BellIcon fontSize="small" color="action" />
                        <Typography variant="body2">{row.original.title}</Typography>
                    </Box>
                ),
            },
            {
                accessorKey: "caseNumber",
                header: "Case",
                size: 200,
                Cell: ({ row }) => (
                    <Box>
                        <Link
                            component="button"
                            onClick={() => handleCaseClick(row.original)}
                            sx={{
                                textDecoration: "none",
                                fontWeight: 500,
                                cursor: row.original.noteId ? "pointer" : "default",
                                color: row.original.noteId ? "primary.main" : "text.secondary",
                                "&:hover": {
                                    textDecoration: row.original.noteId ? "underline" : "none",
                                },
                            }}
                            disabled={!row.original.noteId}
                        >
                            #{row.original.caseNumber}
                        </Link>
                        <Typography variant="caption" display="block" color="text.secondary">
                            {row.original.clientName}
                        </Typography>
                    </Box>
                ),
            },
            {
                accessorKey: "status",
                header: "Status",
                size: 100,
                Cell: ({ row }) => (
                    <Chip
                        label={row.original.status.toUpperCase()}
                        size="small"
                        sx={{
                            bgcolor: row.original.status === "unread" ? "#0f8bed" : "#e0e0e0",
                            color: row.original.status === "unread" ? "#fff" : "#000",
                        }}
                    />
                ),
            },
            {
                accessorKey: "recipients",
                header: "Recipients",
                size: 200,
                Cell: ({ row }) => (
                    <Box>
                        {row.original.recipients.map((recipient, idx) => (
                            <Typography key={idx} variant="body2" sx={{ lineHeight: 1.8 }}>
                                {recipient}
                            </Typography>
                        ))}
                    </Box>
                ),
            },
            {
                accessorKey: "sentBy",
                header: "Sent By",
                size: 150,
                Cell: ({ row }) => (
                    <Box display="flex" alignItems="center" gap={1}>
                        <PersonIcon fontSize="small" color="action" />
                        <Typography variant="body2">{row.original.sentBy}</Typography>
                    </Box>
                ),
            },
            {
                accessorKey: "sentAt",
                header: "Sent At",
                size: 180,
                Cell: ({ row }) => (
                    <Box display="flex" alignItems="center" gap={1}>
                        <CalendarIcon fontSize="small" color="action" />
                        <Typography variant="body2">
                            {formatDateTime(row.original.sentAt)}
                        </Typography>
                    </Box>
                ),
            },
            {
                accessorKey: "readAt",
                header: "Read At",
                size: 180,
                Cell: ({ row }) => (
                    <Typography
                        variant="body2"
                        color={row.original.readAt ? "text.primary" : "text.disabled"}
                    >
                        {row.original.readAt ? formatDateTime(row.original.readAt) : "Not read"}
                    </Typography>
                ),
            },
        ],
        [formatDateTime],
    );

    // CSV export handler
    const handleExportData = () => {
        const exportData = filteredData.map((notification) => ({
            Priority: notification.priority.toUpperCase(),
            Title: notification.title,
            "Case Number": notification.caseNumber,
            "Client Name": notification.clientName,
            Status: notification.status.toUpperCase(),
            Recipients: notification.recipients.join("; "),
            "Sent By": notification.sentBy,
            "Sent At": formatDateTime(notification.sentAt),
            "Read At": notification.readAt ? formatDateTime(notification.readAt) : "Not read",
        }));
        const csv = generateCsv(csvConfig)(exportData);
        download(csvConfig)(csv);
    };

    const table = useMaterialReactTable({
        columns,
        data: filteredData,
        initialState: {
            showColumnFilters: false,
            showGlobalFilter: false,
            density: "compact",
            pagination: { pageIndex: 0, pageSize: 25 },
        },
        enableGlobalFilter: false,
        enableColumnFilters: false,
        enableDensityToggle: false,
        enableFullScreenToggle: false,
        enableHiding: false,
        state: {
            isLoading,
            showAlertBanner: isError,
            showProgressBars: isRefetching,
        },
        muiToolbarAlertBannerProps: isError
            ? {
                  color: "error",
                  children: "Error loading notification history",
              }
            : undefined,
    });

    return (
        <Box>
            {/* Page Header */}
            <Typography variant="h4" mb={1} sx={{ fontSize: "2rem" }}>
                Notification History
            </Typography>
            <Typography variant="body1" color="text.secondary" mb={3}>
                View and manage system notifications. Filter by priority, status, date range, or
                search across all notification data.
            </Typography>

            {/* Admin Alert */}
            {isAdmin && (
                <Alert severity="info" sx={{ mb: 3 }}>
                    Admin View: You can see all system notifications across all cases and users.
                    Team members will only see assigned notifications.
                </Alert>
            )}

            {/* Statistics Cards */}
            <Grid2 container spacing={2} mb={3}>
                <Grid2 size={{ xs: 12, sm: 6, md: 2.4 }}>
                    <Card>
                        <CardContent>
                            <Typography variant="h4" color="primary">
                                {stats.total}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                Total Notifications
                            </Typography>
                        </CardContent>
                    </Card>
                </Grid2>
                <Grid2 size={{ xs: 12, sm: 6, md: 2.4 }}>
                    <Card>
                        <CardContent>
                            <Typography variant="h4" color="primary">
                                {stats.unread}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                Unread Notifications
                            </Typography>
                        </CardContent>
                    </Card>
                </Grid2>
                <Grid2 size={{ xs: 12, sm: 6, md: 2.4 }}>
                    <Card>
                        <CardContent>
                            <Typography variant="h4" color="error">
                                {stats.high}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                High Priority
                            </Typography>
                        </CardContent>
                    </Card>
                </Grid2>
                <Grid2 size={{ xs: 12, sm: 6, md: 2.4 }}>
                    <Card>
                        <CardContent>
                            <Typography variant="h4" color="warning.main">
                                {stats.medium}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                Medium Priority
                            </Typography>
                        </CardContent>
                    </Card>
                </Grid2>
                <Grid2 size={{ xs: 12, sm: 6, md: 2.4 }}>
                    <Card>
                        <CardContent>
                            <Typography variant="h4" color="success.main">
                                {stats.low}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                Low Priority
                            </Typography>
                        </CardContent>
                    </Card>
                </Grid2>
            </Grid2>

            {/* Filters */}
            <Card sx={{ mb: 3 }}>
                <CardContent>
                    <Grid2 container spacing={2}>
                        <Grid2 size={{ xs: 12, md: 2 }}>
                            <TextField
                                fullWidth
                                label="Search notifications, cases, or users..."
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                                size="small"
                            />
                        </Grid2>
                        <Grid2 size={{ xs: 12, sm: 6, md: 2 }}>
                            <TextField
                                fullWidth
                                select
                                label="Priority"
                                value={priorityFilter}
                                onChange={(e) => setPriorityFilter(e.target.value)}
                                size="small"
                            >
                                <MenuItem value="all">All</MenuItem>
                                <MenuItem value="high">High</MenuItem>
                                <MenuItem value="medium">Medium</MenuItem>
                                <MenuItem value="low">Low</MenuItem>
                            </TextField>
                        </Grid2>
                        <Grid2 size={{ xs: 12, sm: 6, md: 2 }}>
                            <TextField
                                fullWidth
                                select
                                label="Status"
                                value={statusFilter}
                                onChange={(e) => setStatusFilter(e.target.value)}
                                size="small"
                            >
                                <MenuItem value="all">All</MenuItem>
                                <MenuItem value="read">Read</MenuItem>
                                <MenuItem value="unread">Unread</MenuItem>
                            </TextField>
                        </Grid2>
                        <Grid2 size={{ xs: 12, sm: 6, md: 2 }}>
                            <TextField
                                fullWidth
                                type="date"
                                label="From Date"
                                value={fromDate}
                                onChange={(e) => setFromDate(e.target.value)}
                                size="small"
                                slotProps={{ inputLabel: { shrink: true } }}
                            />
                        </Grid2>
                        <Grid2 size={{ xs: 12, sm: 6, md: 2 }}>
                            <TextField
                                fullWidth
                                type="date"
                                label="To Date"
                                value={toDate}
                                onChange={(e) => setToDate(e.target.value)}
                                size="small"
                                slotProps={{ inputLabel: { shrink: true } }}
                            />
                        </Grid2>
                        <Grid2 size={{ xs: 12, md: 2 }}>
                            <Button
                                fullWidth
                                variant="contained"
                                startIcon={<FileDownloadIcon />}
                                onClick={handleExportData}
                                disabled={filteredData.length === 0}
                                size="small"
                                sx={{
                                    whiteSpace: "nowrap",
                                }}
                            >
                                Export CSV
                            </Button>
                        </Grid2>
                    </Grid2>
                </CardContent>
            </Card>

            {/* Table */}
            <Stack>
                {filteredData.length === 0 && !isLoading && (
                    <Alert severity="info" sx={{ mb: 2 }}>
                        {notifications.length === 0
                            ? "No notifications found."
                            : "No notifications match your filter criteria."}
                    </Alert>
                )}
                <MaterialReactTable table={table} />
            </Stack>

            {/* Note Detail Modal */}
            <NoteDetailModal
                open={isNoteModalOpen}
                onClose={handleCloseNoteModal}
                noteId={selectedNotification?.noteId ?? null}
                caseNumber={selectedNotification?.caseNumber ?? ""}
                clientName={selectedNotification?.clientName ?? ""}
            />
        </Box>
    );
};

export default NotificationHistoryPage;
